import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Brain, X, Sparkles, BarChart3, RefreshCw, Loader2, Calendar, Clock, AlertCircle, ChevronDown,
} from 'lucide-react';
import DailyAnalyticsContent from './ai/DailyAnalyticsContent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = [
  { id: 'daily-analytics', label: 'Daily Analytics', Icon: BarChart3 },
];

function ErrorCard({ message, onRetry }) {
  return (
    <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 text-center max-w-md mx-auto">
      <AlertCircle className="w-6 h-6 text-rose-500 mx-auto mb-1.5" />
      <p className="text-[13px] text-rose-700 font-medium">{message}</p>
      <button onClick={onRetry} className="mt-2 px-3 py-1.5 rounded-lg bg-white border border-rose-200 text-rose-700 text-[12px] font-medium hover:bg-rose-50">
        Retry
      </button>
    </div>
  );
}

export default function AIAssistantFAB({ user }) {
  const [open, setOpen] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState(TABS[0].id);

  const [now, setNow] = React.useState(new Date());
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState(null);

  // Esc closes
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // Live clock
  React.useEffect(() => {
    if (!open) return;
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [open]);

  // Lock background scroll when modal is open
  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const res = await axios.get(`${API}/dashboard/daily-analytics`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setData(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(detail || 'Failed to load daily analytics');
      if (e?.response?.status === 403) {
        toast.error('AI Assistant is available to Company Admin only');
      } else if (detail) {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on first open + on refresh
  React.useEffect(() => {
    if (open && !data && !loading) fetchData();
  }, [open, data, loading, fetchData]);

  // Belt-and-suspenders role gate
  if (!user || user.role_name !== 'Company Admin') return null;

  const todayStr = now.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  const timeStr = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });

  return (
    <>
      {/* Top-Center Pull-Tab Handle — minimal, hidden-by-default trigger.
          Sized for comfortable tap target (~64×11 visible strip + larger chevron),
          while still sitting flush with top:0 so the page header below it is fully
          readable. Hover/focus reveals the "AI Assistant" tooltip below. */}
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="ai-assistant-rail-tab"
        title="AI Assistant"
        aria-label="Open AI Assistant"
        className={`group fixed left-1/2 -translate-x-1/2 z-40 flex flex-col items-center justify-start pt-0 pb-1 px-2 transition-all duration-300 ${
          open ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
        style={{ top: 0, width: 84, height: 32 }}
      >
        {/* The visible strip — wider, taller, with a small chevron sitting on it */}
        <span
          className="relative flex items-center justify-center rounded-b-full transition-all duration-300 ai-pulltab-strip group-hover:h-[14px] group-hover:w-[72px] group-hover:shadow-[0_4px_18px_rgba(124,58,237,0.55)]"
          style={{
            width: 64,
            height: 11,
            background: 'linear-gradient(90deg, #4F46E5 0%, #7C3AED 50%, #DB2777 100%)',
            boxShadow: '0 3px 10px rgba(124,58,237,0.35)',
          }}
        >
          <ChevronDown className="w-3 h-3 text-white/95" strokeWidth={3} />
        </span>
        {/* Tooltip — appears on hover/focus only */}
        <span className="mt-1 inline-flex items-center gap-1 text-[10.5px] font-semibold tracking-wider uppercase text-violet-700 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
          <Sparkles className="w-2.5 h-2.5" />
          AI Assistant
        </span>
      </button>

      {/* Subtle pulse aura on the strip — draws the eye without being noisy */}
      <style>{`
        @keyframes ai-pulltab-pulse {
          0%, 100% { filter: drop-shadow(0 0 0 rgba(124,58,237,0)); }
          50% { filter: drop-shadow(0 0 8px rgba(124,58,237,0.65)); }
        }
        .ai-pulltab-strip { animation: ai-pulltab-pulse 3s ease-in-out infinite; }
      `}</style>

      {open && (
        <>
          {/* Centered modal — does NOT touch the right edge, never overlaps the Tasks rail */}
          <div
            data-testid="ai-assistant-backdrop"
            className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[60] transition-opacity duration-200"
            onClick={() => setOpen(false)}
          />
          <div className="fixed inset-0 z-[61] flex items-start justify-center pt-12 sm:pt-16 px-3 sm:px-6 pointer-events-none">
            <section
              data-testid="ai-assistant-panel"
              role="dialog"
              aria-modal="true"
              aria-label="AI Assistant — Daily Analytics"
              className="pointer-events-auto w-full max-w-6xl bg-slate-50 rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
              style={{ maxHeight: 'calc(100vh - 96px)' }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Gradient header strip */}
              <div
                className="px-5 py-4 flex items-center gap-3 border-b border-white/20"
                style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 55%, #DB2777 100%)' }}
              >
                <div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center ring-1 ring-white/30">
                  <Brain className="w-4.5 h-4.5 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-[16px] font-semibold text-white tracking-tight leading-tight">AI Assistant</h3>
                  <p className="text-[11.5px] text-white/85 leading-tight inline-flex items-center gap-1.5">
                    <Calendar size={11} /> {todayStr}
                    <span className="opacity-50">•</span>
                    <Clock size={11} /> <span data-testid="ai-clock">{timeStr}</span>
                  </p>
                </div>
                <button
                  onClick={fetchData}
                  disabled={loading}
                  data-testid="ai-refresh"
                  title="Refresh"
                  className="p-2 rounded-lg text-white/85 hover:text-white hover:bg-white/15 transition-colors disabled:opacity-50 inline-flex items-center gap-1.5 text-[12px]"
                >
                  <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                  <span className="hidden sm:inline">Refresh</span>
                </button>
                <button
                  onClick={() => setOpen(false)}
                  data-testid="ai-assistant-close"
                  className="p-2 rounded-lg text-white/85 hover:text-white hover:bg-white/15 transition-colors"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Tab Navigation */}
              <div className="px-4 pt-3 border-b border-slate-200 bg-white" data-testid="ai-assistant-tabs">
                <div className="flex items-center gap-1 overflow-x-auto">
                  {TABS.map((t) => {
                    const TIcon = t.Icon;
                    const isActive = t.id === activeTab;
                    return (
                      <button
                        key={t.id}
                        onClick={() => setActiveTab(t.id)}
                        data-testid={`ai-tab-${t.id}`}
                        className={`px-3.5 py-2 rounded-t-lg text-[12.5px] font-medium whitespace-nowrap inline-flex items-center gap-1.5 transition-all -mb-px border-b-2 ${
                          isActive
                            ? 'text-violet-700 border-violet-500 bg-white'
                            : 'text-slate-500 border-transparent hover:text-violet-600'
                        }`}
                      >
                        <TIcon size={13} /> {t.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Tab Content — scrollable, horizontal layout inside */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-5" data-testid="ai-assistant-content">
                {loading && !data ? (
                  <div className="h-64 flex items-center justify-center">
                    <Loader2 className="w-7 h-7 animate-spin text-violet-500" />
                  </div>
                ) : err && !data ? (
                  <ErrorCard message={err} onRetry={fetchData} />
                ) : data ? (
                  <DailyAnalyticsContent data={data} />
                ) : null}
              </div>
            </section>
          </div>
        </>
      )}
    </>
  );
}
